import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge, EditArticleButton, StatusDot } from '@/components/atoms';
import { formatarNumero, formatarDataHora } from '@/i18n/formato';
import { useArticles, useCategories } from '@/hooks/useArticles';

import styles from './NCNews.module.css';

interface Editoria {
  categoryId: number;
  name: string;
  workflowName: string;
  description: string;
}

const EDITORIAS: Editoria[] = [
  {
    categoryId: 13,
    name: 'Esportes',
    workflowName: 'NCNews Esportes',
    description: 'Schedule 8h, Google News RSS Esportes',
  },
  {
    categoryId: 4,
    name: 'Mercado',
    workflowName: 'NCNews Mercado',
    description: 'Schedule 8h, Google News RSS Mercado',
  },
  {
    categoryId: 5,
    name: 'Mundo',
    workflowName: 'NCNews Mundo',
    description: 'Schedule 8h, Google News RSS Mundo',
  },
  {
    categoryId: 99,
    name: 'Tecnologia',
    workflowName: 'NCNews Tecnologia',
    description: 'Schedule 8h, Google News RSS Tecnologia',
  },
];

const ULTIMAS_CATEGORY_ID = 14;

function isoToday(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function isoNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

interface EditoriaCardProps {
  editoria: Editoria;
}

function EditoriaCard({ editoria }: EditoriaCardProps) {
  const todayQuery = useArticles({
    categories: [editoria.categoryId],
    after: isoToday(),
    per_page: 100,
  });
  const recentQuery = useArticles({
    categories: [editoria.categoryId],
    per_page: 5,
  });
  const weekQuery = useArticles({
    categories: [editoria.categoryId],
    after: isoNDaysAgo(7),
    per_page: 100,
  });

  const recents = recentQuery.data?.items ?? [];
  const todayCount = todayQuery.data?.total ?? 0;
  const weekCount = weekQuery.data?.total ?? 0;

  return (
    <article className={styles.card}>
      <header className={styles.cardHead}>
        <div>
          <h2 className={styles.cardTitle}>{editoria.name}</h2>
          <p className={styles.muted}>{editoria.description}</p>
        </div>
        <Badge tone="neutral" size="xs">
          cat #{editoria.categoryId}
        </Badge>
      </header>

      <div className={styles.metrics}>
        <div className={styles.metric}>
          <span className={styles.metricNumber}>{todayCount}</span>
          <span className={styles.metricLabel}>hoje</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricNumber}>{weekCount}</span>
          <span className={styles.metricLabel}>7d</span>
        </div>
      </div>

      <section>
        <p className={styles.sectionTitle}>Últimos publicados</p>
        {recents.length === 0 && <p className={styles.muted}>sem dados</p>}
        <ul className={styles.recentList}>
          {recents.map((article) => (
            <li key={article.id} className={styles.recentRow}>
              <StatusDot state="ok" />
              <Link to={`/artigos/${article.id}?site=${article.site}`} className={styles.recentLink}>
                {article.title}
              </Link>
              <span className={styles.recentTime}>
                {formatarDataHora(article.published_at, {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              <EditArticleButton id={article.id} size="sm" variant="ghost" />
            </li>
          ))}
        </ul>
      </section>
    </article>
  );
}

function UltimasPanel() {
  const { t } = useTranslation(['articles', 'common']);
  const todayQuery = useArticles({
    categories: [ULTIMAS_CATEGORY_ID],
    after: isoToday(),
    per_page: 100,
  });
  const recentQuery = useArticles({
    categories: [ULTIMAS_CATEGORY_ID],
    per_page: 8,
  });
  const weekQuery = useArticles({
    categories: [ULTIMAS_CATEGORY_ID],
    after: isoNDaysAgo(7),
    per_page: 100,
  });

  const recents = recentQuery.data?.items ?? [];
  const todayCount = todayQuery.data?.total ?? 0;
  const weekCount = weekQuery.data?.total ?? 0;
  const totalCount = recentQuery.data?.total ?? 0;

  return (
    <article className={styles.cardWide}>
      <header className={styles.cardHead}>
        <div>
          <h2 className={styles.cardTitle}>{t('articles:ncnews.ultimas_title')}</h2>
          <p className={styles.muted}>
            {t('articles:ncnews.ultimas_desc')}
          </p>
        </div>
        <Badge tone="info" size="xs">
          {t('articles:ncnews.cat_label', { id: ULTIMAS_CATEGORY_ID })}
        </Badge>
      </header>

      <div className={styles.metricsWide}>
        <div className={styles.metric}>
          <span className={styles.metricNumber}>{todayCount}</span>
          <span className={styles.metricLabel}>hoje</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricNumber}>{weekCount}</span>
          <span className={styles.metricLabel}>7d</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricNumber}>{formatarNumero(totalCount)}</span>
          <span className={styles.metricLabel}>total</span>
        </div>
      </div>

      <section>
        <p className={styles.sectionTitle}>{t('articles:ncnews.latest_published')}</p>
        <ul className={styles.recentList}>
          {recents.map((article) => (
            <li key={article.id} className={styles.recentRow}>
              <StatusDot state="ok" />
              <Link to={`/artigos/${article.id}?site=${article.site}`} className={styles.recentLink}>
                {article.title}
              </Link>
              <span className={styles.recentTime}>
                {formatarDataHora(article.published_at, {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              <EditArticleButton id={article.id} size="sm" variant="ghost" />
            </li>
          ))}
        </ul>
      </section>
    </article>
  );
}

export function NCNews() {
  const { t } = useTranslation(['articles', 'common']);
  const { data: categories } = useCategories();

  const topCategoryTotal = useMemo(() => {
    if (!categories) return 0;
    return EDITORIAS.reduce((sum, ed) => {
      const cat = categories.find((c) => c.id === ed.categoryId);
      return sum + (cat?.count ?? 0);
    }, 0);
  }, [categories]);

  return (
    <section>
      <header className={styles.head}>
        <h1 className={styles.title}>{t('articles:ncnews.title')}</h1>
        <Badge tone="info" size="sm">
          {t('articles:ncnews.editorias_count', { count: EDITORIAS.length })}
        </Badge>
        {topCategoryTotal > 0 && (
          <span className={styles.totalBadge}>
            {t('articles:ncnews.articles_in_archive', { total: formatarNumero(topCategoryTotal) })}
          </span>
        )}
      </header>

      <h2 className={styles.sectionTitle}>{t('articles:ncnews.editorias')}</h2>
      <div className={styles.grid}>
        {EDITORIAS.map((ed) => (
          <EditoriaCard key={ed.categoryId} editoria={ed} />
        ))}
      </div>

      <h2 className={styles.sectionTitle} style={{ marginTop: 'var(--sp-6)' }}>
        Pipeline avançado
      </h2>
      <UltimasPanel />
    </section>
  );
}
